import axios from 'axios';
import {
    allocateFolioAndEnqueue,
    cachePosSnapshot,
    getCachedPosSnapshot,
    idbGetAll,
    idbPut,
    toStorable,
} from '@/utils/posOffline/db';
import { getOrCreateDeviceUuid } from '@/utils/posOffline/device';
import { enrollDeviceIfNeeded } from '@/utils/posOffline/deviceEnrollment';

export const SCHEMA_VERSION = 1;
export const APP_VERSION = '1.0.0';

export const SYNC_STATES = [
    'pending',
    'syncing',
    'synced',
    'synced_with_warnings',
    'auth_required',
    'caja_mismatch',
    'failed',
    'upgrade_required',
    'discarded',
];

export async function addSaleToOutbox(salePayload) {
    const device_uuid = await getOrCreateDeviceUuid();
    const entry = {
        client_mutation_id: salePayload.client_mutation_id || crypto.randomUUID(),
        device_uuid,
        schema_version: SCHEMA_VERSION,
        app_version: APP_VERSION,
        ticket_folio: salePayload.ticket_folio || null,
        payload: salePayload,
        sync_status: 'pending',
        printed: !!salePayload.printed,
        created_at: new Date().toISOString(),
        server_timestamp: salePayload.server_timestamp || null,
    };
    const storable = toStorable(entry);
    await idbPut('outbox', storable);
    notifyOutboxChange({ action: 'enqueue', id: storable.client_mutation_id });
    return storable;
}

export async function enqueueOfflineSale(salePayload, deviceRecord = {}) {
    const device_uuid = deviceRecord.device_uuid || (await getOrCreateDeviceUuid());
    const clientMutationId = salePayload.client_mutation_id || crypto.randomUUID();
    const entry = {
        client_mutation_id: clientMutationId,
        device_uuid,
        schema_version: SCHEMA_VERSION,
        app_version: APP_VERSION,
        payload: {
            ...salePayload,
            client_mutation_id: clientMutationId,
            device_uuid,
            schema_version: SCHEMA_VERSION,
            app_version: APP_VERSION,
        },
        sync_status: 'pending',
        printed: false,
        created_at: new Date().toISOString(),
        server_timestamp: salePayload.server_timestamp || null,
    };
    return allocateFolioAndEnqueue({
        deviceUuid: device_uuid,
        deviceCode: deviceRecord.codigo,
        negocio: salePayload.usuario_negocio || deviceRecord.usuario_negocio || null,
        entry,
    }).then((row) => {
        notifyOutboxChange({ action: 'enqueue', id: row.client_mutation_id });
        return row;
    });
}

export async function listOutbox(statusFilter) {
    const all = await idbGetAll('outbox');
    if (!statusFilter) return all.sort((a, b) => a.created_at.localeCompare(b.created_at));
    return all.filter((x) => x.sync_status === statusFilter).sort((a, b) => a.created_at.localeCompare(b.created_at));
}

export async function updateOutboxEntry(id, patch) {
    const all = await idbGetAll('outbox');
    const row = all.find((x) => x.client_mutation_id === id);
    if (!row) return null;
    const next = toStorable({ ...row, ...patch });
    await idbPut('outbox', next);
    notifyOutboxChange({ action: 'update', id: next.client_mutation_id });
    return next;
}

export async function countPendingOutbox() {
    const pending = await listOutbox('pending');
    const failed = await listOutbox('failed');
    const auth = await listOutbox('auth_required');
    const caja = await listOutbox('caja_mismatch');
    const upgrade = await listOutbox('upgrade_required');
    return pending.length + failed.length + auth.length + caja.length + upgrade.length;
}

export async function exportOutboxBackup() {
    const all = await idbGetAll('outbox');
    return JSON.stringify({ exported_at: new Date().toISOString(), sales: all }, null, 2);
}

export function outboxEntryToCobroRow(entry) {
    const payload = entry.payload || {};
    const items = (payload.items || []).map((it) => ({
        producto_nombre: it.producto_nombre,
        cantidadSeleccionada: it.cantidadSeleccionada,
        producto_precio: it.producto_precio,
    }));
    return {
        pedido_id: `offline:${entry.client_mutation_id}`,
        offline: true,
        client_mutation_id: entry.client_mutation_id,
        sync_status: entry.sync_status,
        ticket_folio: entry.ticket_folio || null,
        cliente_nombre: payload.cliente_nombre || 'Consumidor final',
        medio_pago: payload.medio_pago || '',
        total: payload.total,
        vendedor: payload.vendedor || '',
        detalle_cliente: payload.detalle_cliente || 'Venta POS',
        creado_en: payload.server_timestamp || entry.created_at,
        items_json: JSON.stringify(items),
    };
}

export async function listOutboxCobros({ includeSynced = false } = {}) {
    const all = await idbGetAll('outbox');
    return all
        .filter((entry) => {
            if (entry.sync_status === 'discarded') return false;
            if (!includeSynced && entry.sync_status === 'synced') return false;
            return true;
        })
        .map(outboxEntryToCobroRow)
        .sort((a, b) => String(b.creado_en).localeCompare(String(a.creado_en)));
}

let syncing = false;
const syncChannel =
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('puestify-pos-sync') : null;

function notifyOutboxChange(detail = {}) {
    if (syncChannel) {
        syncChannel.postMessage({ type: 'outbox_changed', ...detail });
    }
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent('pos:outbox-updated', { detail }));
    }
}

/** Escucha cambios de outbox/sync entre pestañas (ventas offline, sync, etc.). */
export function subscribePosSyncEvents(fn) {
    if (!syncChannel) return () => {};
    const handler = (ev) => {
        try {
            fn(ev.data || {});
        } catch (_) {
            /* ignore */
        }
    };
    syncChannel.addEventListener('message', handler);
    return () => syncChannel.removeEventListener('message', handler);
}

async function postPedidoWithDeviceRecovery(body, item) {
    try {
        return await axios.post('/pedidos', body);
    } catch (e) {
        const code = e.response && e.response.data && e.response.data.code;
        const negocio = body.usuario_negocio || (item.payload && item.payload.usuario_negocio);
        if (code !== 'DEVICE_NOT_FOUND' || !negocio) {
            throw e;
        }
        await enrollDeviceIfNeeded({ negocio, force: true });
        const device_uuid = await getOrCreateDeviceUuid();
        const retryBody = { ...body, device_uuid };
        return axios.post('/pedidos', retryBody);
    }
}

async function syncOutboxCore({ onProgress } = {}) {
    if (syncing) return { skipped: true };
    syncing = true;
    const results = { synced: 0, errors: [] };
    try {
        const queue = await listOutbox();
        const todo = queue.filter((x) => ['pending', 'failed', 'auth_required'].includes(x.sync_status));
        for (const item of todo) {
            await updateOutboxEntry(item.client_mutation_id, { sync_status: 'syncing' });
            try {
                const body = {
                    ...item.payload,
                    client_mutation_id: item.client_mutation_id,
                    device_uuid: item.device_uuid,
                    schema_version: item.schema_version,
                    app_version: item.app_version,
                    origen: 'pos',
                };
                const { data } = await postPedidoWithDeviceRecovery(body, item);
                if (data && ['DEVICE_REVOKED', 'DEVICE_REPLACED'].includes(data.code)) {
                    await updateOutboxEntry(item.client_mutation_id, { sync_status: 'pending' });
                    throw Object.assign(new Error(data.code), { code: data.code });
                }
                const warnings = (data && data.warnings) || [];
                await updateOutboxEntry(item.client_mutation_id, {
                    sync_status: warnings.length ? 'synced_with_warnings' : 'synced',
                    pedido_id: data.pedido_id,
                    warnings,
                    synced_at: new Date().toISOString(),
                });
                results.synced += 1;
                if (syncChannel) syncChannel.postMessage({ type: 'synced', id: item.client_mutation_id });
                notifyOutboxChange({ action: 'synced', id: item.client_mutation_id });
                if (onProgress) onProgress({ item, ok: true });
            } catch (e) {
                const code = e.response && e.response.data && e.response.data.code;
                let st = 'failed';
                if (e.response && e.response.status === 401) st = 'auth_required';
                else if (code === 'CAJA_MISMATCH') st = 'caja_mismatch';
                else if (code === 'SCHEMA_VERSION_UNSUPPORTED') st = 'upgrade_required';
                else if (code === 'DEVICE_REVOKED' || code === 'DEVICE_REPLACED') {
                    syncing = false;
                    throw e;
                }
                await updateOutboxEntry(item.client_mutation_id, {
                    sync_status: st,
                    last_error: (e.response && e.response.data && e.response.data.message) || e.message,
                });
                results.errors.push({ id: item.client_mutation_id, status: st, message: e.message });
                if (onProgress) onProgress({ item, ok: false, error: e });
            }
        }
    } finally {
        syncing = false;
    }
    return results;
}

export async function syncOutbox(options = {}) {
    if (typeof navigator !== 'undefined' && navigator.locks && navigator.locks.request) {
        return navigator.locks.request(
            'puestify-pos-sync',
            { ifAvailable: true },
            (lock) => (lock ? syncOutboxCore(options) : { skipped: true, reason: 'other_tab' })
        );
    }
    return syncOutboxCore(options);
}

export async function resolveCajaMismatch(clientMutationId, currentCajaId) {
    const all = await idbGetAll('outbox');
    const row = all.find((item) => item.client_mutation_id === clientMutationId);
    if (!row || row.sync_status !== 'caja_mismatch') return null;
    return updateOutboxEntry(clientMutationId, {
        payload: {
            ...row.payload,
            expected_caja_id: currentCajaId,
            resolve_caja_mismatch: 'attach_current',
        },
        sync_status: 'pending',
        last_error: null,
    });
}

export async function discardOutboxEntry(clientMutationId, reason) {
    return updateOutboxEntry(clientMutationId, {
        sync_status: 'discarded',
        discarded_at: new Date().toISOString(),
        discard_reason: reason || 'Anulada por responsable',
    });
}

export async function listOutboxConflicts() {
    const all = await idbGetAll('outbox');
    return all.filter((item) => ['caja_mismatch', 'upgrade_required', 'synced_with_warnings'].includes(item.sync_status));
}

function compareVersions(left, right) {
    const a = String(left || '0').split('.').map((x) => Number(x) || 0);
    const b = String(right || '0').split('.').map((x) => Number(x) || 0);
    for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
        if ((a[i] || 0) !== (b[i] || 0)) return (a[i] || 0) - (b[i] || 0);
    }
    return 0;
}

export async function checkServerTimeOffset() {
    try {
        const { data } = await axios.get('/devices/server-time');
        const serverMs = new Date(data.server_time).getTime();
        const localMs = Date.now();
        const offsetMs = serverMs - localMs;
        const thresholdMs = 5 * 60 * 1000;
        const supported = Array.isArray(data.supported_schema_versions)
            ? data.supported_schema_versions.map(Number)
            : [];
        const upgradeRequired =
            !supported.includes(SCHEMA_VERSION) ||
            compareVersions(APP_VERSION, data.min_pos_version) < 0;
        await cachePosSnapshot('server_contract', {
            offsetMs,
            server_time: data.server_time,
            checked_at: new Date().toISOString(),
            supported_schema_versions: supported,
            min_pos_version: data.min_pos_version,
        });
        return {
            offsetMs,
            warning: Math.abs(offsetMs) > thresholdMs,
            server_time: data.server_time,
            supported_schema_versions: supported,
            min_pos_version: data.min_pos_version,
            upgradeRequired,
        };
    } catch {
        const cached = await getCachedPosSnapshot('server_contract');
        const supported = (cached && cached.supported_schema_versions) || [SCHEMA_VERSION];
        const minVer = (cached && cached.min_pos_version) || APP_VERSION;
        const upgradeRequired = cached
            ? (!supported.includes(SCHEMA_VERSION) || compareVersions(APP_VERSION, minVer) < 0)
            : false;
        return {
            offsetMs: (cached && cached.offsetMs) || 0,
            warning: false,
            cached: true,
            supported_schema_versions: supported,
            min_pos_version: minVer,
            upgradeRequired,
        };
    }
}
